import { Component, ElementRef, HostListener, OnInit, ViewChild, OnDestroy, Renderer2, Inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';
import { DOCUMENT, CommonModule } from '@angular/common';

@Component({
  selector: 'app-ai-coding-tools-for-developers',
  templateUrl: './ai-coding-tools-for-developers.component.html',
  styleUrls: ['./ai-coding-tools-for-developers.component.scss']
})
export class AiCodingToolsForDevelopersComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router, 
    private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  
  private jsonLdScript: any;

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

injectSchemaMarkup(): void {
    const fullGraphSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/ai-coding-tools-for-developers",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/ai-coding-tools-for-developers",
          "name": "10 Best AI Coding Tools for Developers in 2026",
          "description": "Discover the top 10 AI coding tools for developers in 2026 — AI tools for coding, AI coding assistants, and enterprise AI developer tools.",
          "inLanguage": "en-US",
          "datePublished": "2026-04-08",
          "dateModified": "2026-04-08",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".padding-headningmain",
              ".softwaredev-heading",
              ".section-title"
            ]
          },
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/ai-coding-tools-for-developers#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://www.kalpitatechnologies.com/tech-blogs/blog" },
            { "@type": "ListItem", "position": 3, "name": "Best AI Coding Tools 2026", "item": "https://www.kalpitatechnologies.com/tech-blogs/ai-coding-tools-for-developers" }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/ai-coding-tools-for-developers#article",
          "headline": "10 Best AI Coding Tools for Developers in 2026",
          "alternativeHeadline": "AI Coding Assistants — A Practical Guide for Engineers and Enterprises",
          "articleBody": "AI coding tools have become an essential part of modern software development in 2026, evolving far beyond simple autocomplete into intelligent agents... leading tools include GitHub Copilot, Cursor, and Claude Code.",
          "author": { "@type": "Person", "name": "Kalpita Technologies Editorial Team" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2026-04-08",
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/ai-coding-tools-for-developers/ai-coding-tools-banner.png"
        },
        {
          "@type": "HowTo",
          "name": "How to Choose the Right AI Coding Tool in 2026",
          "step": [
            { "@type": "HowToStep", "name": "Map Your Workflow", "text": "Audit top time sinks in your IDE and version control processes." },
            { "@type": "HowToStep", "name": "Security Check", "text": "Verify if code needs to remain on-prem or can be sent to private cloud models." },
            { "@type": "HowToStep", "name": "Size Alignment", "text": "Shortlist based on team size—Cursor for individuals, Copilot for enterprise." },
            { "@type": "HowToStep", "name": "Pilot Test", "text": "Run a 2-week comparison focusing on velocity and developer experience." },
            { "@type": "HowToStep", "name": "Scaling", "text": "Deploy with prompt-pattern training and ROI metrics." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Do AI coding tools replace developers?",
              "acceptedAnswer": { "@type": "Answer", "text": "No. AI coding tools amplify productivity by handling boilerplate and refactors, but humans remain owners of architecture, security, and logic validation." }
            },
            {
              "@type": "Question",
              "name": "Which tool leads in developer adoption for 2026?",
              "acceptedAnswer": { "@type": "Answer", "text": "GitHub Copilot remains the most mature and widely used, while Cursor is preferred for multi-file repository understanding." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
          }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(fullGraphSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  nav(url: any) {
    this.router.navigateByUrl(url)
  }

  scrollToSection(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const yOffset = 5 * 16;
    const top = el.getBoundingClientRect().top + window.pageYOffset - yOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}
